// Player controller — ONE physics body driven by two input frontends (flat +
// VR). All feel constants come from tunables (live-pushed by the server).
// States: ground | air | climb | glide (wire name — the JET-SUIT: it ignites
// by itself after a beat of airtime and thrusts toward where you look).
import * as THREE from 'three';
import { STEP_UP, STEP_EPS } from '../../game/structcol.js';
import { StickGate } from './vrstick.js';

// a structure ceiling (game/structcol.js ceilingAt) this close over
// the feet is a roof for the jet-suit ignition: the tallest lining crown is
// 5.85 m (params.emit.fixed rail 5.1 m + arch), a girder over a lane stands
// higher only where the jet may fly under it
const ROOF_JET_M = 8;
// the integrator's largest step (s): a longer frame runs sub-steps (Player.step)
export const STEP_MAX_S = 0.05;
// WAYSG-L3 (owner: "some stairs can have steeper steps … exceptions, but
// possible"): on a drawn GROUND FLIGHT (ways2 flightAt) the slope brake
// stands at this stair limit instead of locomotion.slope_walk_max_deg — a
// ground flight's ground is its plane, so a 45 deg flight reads 45 deg and the
// open-slope brake (42) refused it. Live flights: slope p95 39 deg, a few
// 41-49 deg. Steeper than this stays a wall (climb territory).
export const GROUND_FLIGHT_WALK_DEG = 50;

export class Player {
  constructor(hf, tunables) {
    this.hf = hf;
    this.tun = tunables;
    this.pos = new THREE.Vector3();
    this.vel = new THREE.Vector3();
    this.yaw = 0;
    this.state = 'ground';
    this.grounded = false;
    this.speed = 0;
    this.eyeHeight = 1.65;   // flat mode; VR uses real head pose
    // Bore crouch. A funicular bore is 3.8 m to the crown but the track climbs
    // at 30-45 %, so wherever the bed crests the roof comes down to meet a
    // standing head, the ceiling clamp caps the body, and a standing eye
    // would ride at the crown seeing straight out of the mountain. Ducking
    // inside low headroom fixes that at the eye.
    // Eased, not snapped — an instant 0.5 m drop of the flat camera reads as
    // falling. VR keeps its real head pose; there the lower head height still
    // means the ceiling clamp shoves the body far less.
    this._crouch = 0;                          // 0 = stood up, 1 = fully ducked
    this.collider = null;    // BuildingCollision, set by main
    this.forest = null;      // Forest (solid trunks), set by main
    // climb
    this.stamina = tunables.climb.stamina_max_s;
    this.climbAnchor = null;      // {hand, worldPos of hand at grab}
    // jet-suit (wire state 'glide')
    this.gliding = false;
    this.glideDir = new THREE.Vector3(0, 0, -1);   // horizontal facing while flying
    this._airT = 0;                            // continuous airborne seconds
    // off-road bike
    this.bike = false;
    this.bikeYaw = 0;
    this.bikeSpeed = 0;
    this.bikeSteer = 0;                        // last steer input (visual handlebar)
    // auto-follow riding mode: lock onto the nearest way (roads/paths/rails,
    // RoadGraph in this.roadnet.graph) and ride its polyline; junctions branch
    // by the turn indicator. Q toggles follow<->manual (Player.toggleFollow).
    this.bikeFollow = false;   // auto-set on mount when a way is within reach
    this.bikeInd = 0;          // turn indicator: -1 left / 0 off / +1 right
    this.roadnet = null;       // WaysDrawNet (carries .graph), set by main
    this._fLock = false;       // locked onto ways[_fw] at vertex _fi, going _fdir
    this._fw = 0; this._fi = 0; this._fdir = 1;
    this._jCool = 0;           // metres of travel until the next junction decision
    this._fGrace = 0;          // metres of keep-the-lock after a branch switch
    this._rampVy = 0;                          // surface-follow vertical speed (ramp launch)
    this.roads = null;                         // roads-mask sampler, set by main
    this.surface = null;                       // AI class-map sampler, set by main
    this.water = null;                         // WaterIndex, set by main
    this.swimming = false;
    this._swimK = 0;          // eased 0..1 swim-presentation blend (eye/audio)
    this._swimSurf = null;    // water sheet height while swimming (VR rig nudge)
    this._swimEyeRel = null;  // flat-mode eye target above pos while swimming
    this.splash = 0;          // water entry/exit impulse 0..1, consumed by main
    this._crashT = 0;                          // jet cut-out after a wall impact
    this.carried = false;                      // funicular owns the position
    this._stuckRef = new THREE.Vector3();      // anti-trap watchdog (_antiStuck)
    this._stuckT = 0;
    this._unstickT = 0;                        // >0 = ejecting, constraints off
    this._lastRes = new THREE.Vector3();       // last collision-resolved xz (swept test)
    // the structure collider (game/structcol.js, APP_BAKER §3 One
    // collider): main attaches it at load; a gate fixture may run without one
    this.structs = null;
    this._sFrom = new THREE.Vector3();         // where this frame's motion started
    this._sFromOk = false;
    this._sOut = {};
    this._gFn = (x, y, z) => this._groundY(x, y, z);
    // dizzy death mode (combat.dizzy_on_kill, server 'dizzy' event): frozen
    // in place for dizzyFreeze s, then dizziness decays over dizzyTotal s —
    // walking only, at a reduced speed, red overlay driven by dizzy01
    this.dizzyFreeze = 0;
    this.dizzyT = 0;
    this.dizzyTotal = 0;
  }

  // server said we were killed in dizzy mode: freeze, then recover slowly
  setDizzy(freezeS, decayS) {
    this.dizzyFreeze = Math.max(0, freezeS || 0);
    this.dizzyT = this.dizzyTotal = Math.max(0.1, decayS || 10);
    this.cancelGlide();
    this.bike = false;
    if (this.state === 'climb') this.endClimb();
    this.vel.set(0, 0, 0);
    this.state = 'ground';
  }

  get dizzyActive() { return this.dizzyFreeze > 0 || this.dizzyT > 0; }

  // 0..1 dizziness for the red overlay: full while frozen, then declining
  get dizzy01() {
    if (this.dizzyFreeze > 0) return 1;
    return this.dizzyTotal > 0 ? this.dizzyT / this.dizzyTotal : 0;
  }

  // walk-speed factor while dizzy, recovering linearly to 1
  _dizzyMult() {
    const c = this.tun.combat || {};
    const m0 = c.dizzy_walk_mult ?? 0.35;
    const f = this.dizzyTotal > 0 ? this.dizzyT / this.dizzyTotal : 0;
    return m0 + (1 - m0) * (1 - f);
  }

  // Impact response against a solid (n = the outward surface normal, i.e. the
  // direction the collider pushed us). The velocity component INTO the surface
  // is destroyed, the tangential slide survives with friction — so a graze
  // barely costs speed and a head-on hit stops you. Returns the closing speed
  // that was absorbed.
  //
  // Blanket `vel *= 0.15` used to stand in for this, which was wrong twice
  // over: on the bike vel is REBUILT from bikeSpeed every frame (so the damp
  // did nothing and you ground along facades at full throttle), and in the
  // jet suit the thrust simply re-accelerated into the wall — you hovered
  // against a building instead of falling off it.
  _impact(nx, nz) {
    const nl = Math.hypot(nx, nz);
    if (nl < 1e-6) return 0;
    nx /= nl; nz /= nl;
    const vn = this.vel.x * nx + this.vel.z * nz;
    if (vn >= -0.05) return 0;                 // already moving away — no hit
    const before = Math.hypot(this.vel.x, this.vel.z) || 1;
    // The tangential slide is kept WHOLE. A per-frame friction factor looks
    // harmless and is not: contact repeats every frame while you slide along a
    // wall, so any factor < 1 decays the slide geometrically (and frame-rate
    // dependently) — a graze bled 20 m/s down to 6 m/s along a straight
    // facade. Speed loss comes from the geometry alone: head-on loses
    // everything, a graze loses nothing.
    this.vel.x -= vn * nx;
    this.vel.z -= vn * nz;
    const closing = -vn;
    const after = Math.hypot(this.vel.x, this.vel.z);
    if (this.bike) {
      const b = this.tun.bike || {};
      this.bikeSpeed *= after / before;        // only the tangential part survives
      if (closing > (b.crash_speed || 7)) this.bikeSpeed *= 0.3;   // real crash
      // and the bike is DEFLECTED along the wall. Without this the rider's
      // heading keeps pointing into it, _steerBike re-creates the inward
      // velocity from bikeYaw every frame and the impact tax is charged again
      // and again — a 21° graze bled 20 m/s to 2 m/s in half a second.
      // Rate-limited: main.js turns the VR rig by the bikeYaw delta, so an
      // unclamped snap would whip the view on contact.
      if (after > 0.5) {
        let d = Math.atan2(-this.vel.x, -this.vel.z) - this.bikeYaw;
        d = ((d + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
        this.bikeYaw += Math.max(-0.26, Math.min(0.26, d));   // ≤15°/frame
      }
    }
    const g = this.tun.glide || {};
    if (this.gliding && closing > (g.crash_speed || 8)) {
      // flying into a wall is a crash, not a hover: cut the jet and let gravity
      // take the body down the facade. _crashT blocks the auto-ignition that
      // would otherwise relight 0.4 s later and fly it straight back up.
      this.cancelGlide();
      this._crashT = g.crash_stun_s || 1.4;
      this.vel.x *= 0.3;
      this.vel.z *= 0.3;
      this.vel.y = Math.min(this.vel.y, 0);
    }
    return closing;
  }

  // Trunk push as a normal: resolveTrunks only reports "hit", so derive the
  // surface normal from the displacement it applied.
  _trunkPush() {
    if (!this.forest) return null;
    const px = this.pos.x, pz = this.pos.z;
    if (!this.forest.resolveTrunks(this.pos)) return null;
    const dx = this.pos.x - px, dz = this.pos.z - pz;
    const l = Math.hypot(dx, dz);
    return l > 1e-6 ? { x: dx / l, z: dz / l } : null;
  }

  // Effective eye height — ducked while the body is inside a bore volume.
  // Everything that asks "where is the head" must go through this, or the
  // clamps and the camera disagree about how tall the player is.
  get eye() {
    const t = this.tun.locomotion || {};
    const low = t.bore_eye_height ?? 1.15;
    let e = this.eyeHeight - (this.eyeHeight - low) * this._crouch;
    // swimming (flat mode; VR nudges the rig in main.js instead): the head
    // rides water.eye_over_m above the SHEET, blended by _swimK so entering /
    // leaving water never snaps the camera. Without this the bare float put
    // the eye ~0.3 m over the plane and the camera near-plane grazed it — a
    // gray wall filling the view.
    if (this._swimK > 0 && this._swimEyeRel !== null) {
      e += (this._swimEyeRel - e) * this._swimK;
    }
    return e;
  }

  // Drive the duck from the span verdict: duck while this column has a roof
  // and the clear height is under the standing eye's needs. Crawl holes
  // make the span answer complete for every passable buried column — a
  // no-verdict column is rock.
  _stepCrouch(dt) {
    const r = this._spanAt(this.pos.x, this.pos.y, this.pos.z);
    const inside = r !== null && r.ceiling !== null
      && r.ceiling - r.ground < this.eyeHeight + 1.0;
    const rate = dt * 4;                       // ~0.25 s in or out
    this._crouch = inside
      ? Math.min(1, this._crouch + rate)
      : Math.max(0, this._crouch - rate);
  }

  // ------------------------------------------------------- the skin
  // The standing skin is the GROUND-M L0 node's triangle under (x, z), the
  // game server's artefact and sampler (ground.js ClientGround.standHeight,
  // vrm_core; a GM03 hole: game/holefall.js at both ends). The foot filter
  // — the disc-mean suspension a body needs on 0.5 m LiDAR — is baked INTO
  // the landform (meta.foot_filter) and the server stands on the same bytes.
  _skinY(x, z) {
    return this.hf.standHeight(x, z);
  }

  // span verdict for the column under (x,z) at body height y, or null (no
  // spans / no hole / tile not resident) — the one call crouch, the ceiling
  // clamp and the watchdog share so they cannot disagree with _groundY
  _spanAt(x, y, z) {
    if (!this.spans) return null;
    const g = this._skinY(x, z);
    if (g === null) return null;
    return this.spans.resolve(x, z, y, g);
  }

  // THE standing surface at (x,z) for a body currently at height y: the terrain
  // skin, overridden by a tunnel bore floor only while the body is genuinely
  // inside a bore. null while the collision tile is not resident.
  // Every clamp in this file goes through here — the walk step, the jet
  // landing and the building re-clamp used to each re-derive it, and a clamp
  // that skipped the bore override would yank a tunnel walker to the summit.
  // float = false: no lake float — the seat of a placed object (furniture.js
  // standOf / seatOf), which never swims; the same query otherwise.
  _groundY(x, y, z, float = true) {
    const g = this._groundY0(x, y, z, float);
    if (g === null || !this.structs) return g;
    // structures are ADDITIVE: the highest structure floor within
    // the step-up of these feet raises the ground, never lowers it
    const s = this.structs.groundAt(x, z, y);
    return s !== null && s > g ? s : g;
  }

  // the lowest roof over a SEAT at y: the spans column's ceiling or the
  // lowest structure sheet above y itself (structcol ceilingAt from
  // y - STEP_UP: its limit is then y + STEP_EPS, so a girder in the body's
  // step band counts — a placed object cannot step over it; a deck soffit, a
  // bore crown) — null = open sky. Not a clamp: only furniture.js seatOf asks
  // it whether an object has room under a level.
  headroomAt(x, y, z) {
    const r = this._spanAt(x, y, z);
    let c = r !== null && r.ceiling !== null ? r.ceiling : null;
    if (this.structs) {
      const s = this.structs.ceilingAt(x, z, y - STEP_UP);
      if (s !== null && (c === null || s < c)) c = s;
    }
    return c;
  }

  // terrain skin + spans + water float
  _groundY0(x, y, z, float = true) {
    const g = this._skinY(x, z);
    if (g === null) return null;
    // SPANS (APP_BAKER.md §6): the bake's per-column holes are THE authority on
    // multi-level ground — the bed inside a bore, the surface on its roof,
    // per column, no y-gates. A hole verdict also wins over the lake float
    // below (rule 8: an underpass under lake level must not float you).
    // A column with no verdict is rock or open ground: the skin.
    if (this.spans) {
      const r = this.spans.resolve(x, z, y, g);
      if (r !== null) return r.ground;
    }
    // Inside a lake the DTM IS the water plane (LiDAR on open water is noise,
    // so the bake flattens it), which made water a solid floor. Float instead:
    // the standing surface is the surface minus a fixed submersion. No
    // bathymetry is invented — depth is not simulated, buoyancy is.
    if (float && this.water) {
      const s = this.water.surfaceAt(x, z);
      if (s !== null && s >= g - 0.25) {
        return s - ((this.tun.water && this.tun.water.submerge_m) || 1.35);
      }
    }
    return g;
  }

  // ---------------------------------------------------------- anti-trap
  // COMPENSATION — lifts a walker out of the ground at S6.d R2 site run 5229 / deck 342 (1754, 5197); removed by m4b-antistuck.diff once check-traversal 'deck parapet holds' is 0/2731 without it
  // LAST LINE OF DEFENCE, and the only one that is geometry-agnostic. Every
  // other resolver here is local and self-consistent — walls push out, the
  // structure collider and spans seat the feet, the terrain clamps up, water
  // floats — and any TWO of them can disagree at a seam and hold the body.
  // So don't enumerate the geometries: watch the OUTCOME. A body that wants to
  // move (or is buried under the terrain, which is never a resting state) and
  // hasn't moved for STUCK_S is by definition trapped. Eject it straight up to
  // the open surface, with every local constraint suspended (`_unstickT`)
  // until it gets there.
  //
  // The rise is rate-limited to stay under Sim's anti-teleport caps
  // (capUp = jump_speed*dt + 0.4): Sim only advances _lastValid on ACCEPTED
  // input, so a single over-cap hop would desync the body permanently and
  // trade one trap for another.
  _antiStuck(dt, wants) {
    // Rise is a RATE capped per frame (0.3 m/frame at the 72 Hz baseline),
    // never a bare per-frame step: a per-frame constant is a different m/s at
    // every headset refresh, and at 90/120 Hz it accumulates past Sim's capUp
    // between two 32 Hz reports. 21.6 m/s reproduces the 72 Hz (and gate
    // 60 Hz) behaviour exactly and only slows the eject at higher refresh.
    const RISE = Math.min(0.3, 21.6 * dt);
    const surf = this._skinY(this.pos.x, this.pos.z);
    if (this._unstickT > 0) {
      this._unstickT -= dt;
      this._escape = null;
      this.cancelGlide();                // thrust into rock fights the lift
      this.vel.set(0, 0, 0);
      // Under a ROOF the open surface is not reachable without passing through
      // the drawn lining — the hole's own bed is the legitimate resting place,
      // so the eject settles DOWN onto it instead of rising through the crown
      // ("head pokes through the ceiling and it spawns back outside"). Only a
      // column with no roof ejects up to the skin.
      const rSpan = this._spanAt(this.pos.x, this.pos.y, this.pos.z);
      const bedY = rSpan !== null && rSpan.ceiling !== null ? rSpan.ground : null;
      const roofed = bedY !== null;
      const target = roofed ? bedY + 0.2
        : (surf !== null ? surf : this.pos.y) + 0.2;
      if (roofed || surf === null || this.pos.y >= target - 0.02
        || this._unstickT <= 0) {
        if (roofed) this.pos.y = Math.max(bedY, Math.min(this.pos.y, target));
        else if (surf !== null) this.pos.y = Math.max(this.pos.y, target);
        this._unstickT = 0;
        this._stuckT = 0;
        this._stuckRef.copy(this.pos);
        this.grounded = true;
        this.state = 'ground';
      } else {
        this.pos.y = Math.min(target, this.pos.y + RISE);
      }
      return;
    }
    // A carried rider is positioned by the funicular, not by physics — and in
    // a deep cutting it sits legitimately below the terrain samples. Ejecting
    // it would fire the player out of the cabin.
    if (this.carried || this.state === 'climb') {
      this._stuckT = 0;
      this._stuckRef.copy(this.pos);
      return;
    }
    const under = surf !== null && this.pos.y < surf - 0.8 && !this.swimming;
    // a body under the skin in a span hole is in a bore, not buried
    const inBore = under && this._spanAt(this.pos.x, this.pos.y, this.pos.z) !== null;
    const buried = under && !inBore;         // no bore, lake or cutting to justify it
    // NB an in-bore body that fails the progress window IS ejected up through
    // the mountain, even standing on the bore floor. Sparing it (a lower "no
    // progress at all" bar while grounded on the floor) was tried and reverted:
    // the pinned body in check-feel.mjs still oscillates ~0.3 m per window, so
    // any bar low enough to spare a slow walker also lets a genuine trap stand.
    // See APP_PLAYER §The anti-trap watchdog.
    if (!wants && !buried) {
      this._stuckT = 0;
      this._stuckRef.copy(this.pos);
      return;
    }
    // NET progress over a whole window, never a per-frame delta: a body being
    // volleyed between two resolvers moves ~0.5 m every frame and arrives
    // nowhere, which a per-frame test reads as "moving fine" — the exact
    // failure mode this watchdog exists to catch.
    this._stuckT += dt;
    if (this._stuckT < 1.5) return;
    const progress = this.pos.distanceTo(this._stuckRef);
    this._stuckT = 0;
    this._stuckRef.copy(this.pos);
    if (progress > 0.6) return;              // 0.6 m in 1.5 s of trying: fine
    if (buried || inBore) {
      this._unstickT = 4;                    // hard budget: never loops forever
    } else if (this.collider) {
      // Hemmed in ON the surface. Leaning into a facade is not a trap — you
      // can always turn around — so no teleport here; just re-arm the
      // collider's own committed escape, which walks out over frames.
      this._escape = this.collider.findFree(this.pos.x, this.pos.y, this.pos.z);
    }
  }


  toggleBike() {
    const b = this.tun.bike;
    if (!b || !b.enabled || this.dizzyActive) return false;
    if (this.bike) { this.bike = false; this.bikeInd = 0; return true; }
    // no mounting mid-lake — entering water force-dismounts (see step)
    if (!this.grounded || this.state === 'climb' || this.gliding
      || this.swimming) return false;
    this.bike = true;
    this.bikeYaw = this.yaw;
    this.bikeSpeed = Math.hypot(this.vel.x, this.vel.z);
    this.bikeInd = 0;
    this._fLock = false; this._jCool = 0; this._fGrace = 0;
    // mounted on/near a way → auto-follow by default; open terrain → manual
    this.bikeFollow = this._canLock();
    return true;
  }

  // follow <-> manual riding mode (the Q key / left Y while on the bike)
  toggleFollow() {
    if (!this.bike) return;
    this.bikeFollow = !this.bikeFollow;
    this._fLock = false; this._jCool = 0; this._fGrace = 0;
  }

  // turn indicator: same side again cancels, the other side replaces
  toggleIndicator(side) {
    if (!this.bike) return;
    this.bikeInd = this.bikeInd === side ? 0 : side;
  }

  _canLock() {
    const g = this.roadnet && this.roadnet.graph;
    if (!g || !g.ways.length) return false;
    return !!g.nearest(this.pos.x, this.pos.y, this.pos.z,
      (this.tun.bike && this.tun.bike.follow_lock_m) || 6, 5);
  }

  setTunables(t) { this.tun = t; }

  spawn(x, y, z) {
    this.pos.set(x, y, z);
    this._lastRes.set(x, 0, z);   // a spawn is not motion — don't sweep to it
    this._escape = null;
    this.vel.set(0, 0, 0);
    this.state = 'ground';
    this.gliding = false;
    this._airT = 0;
    this.climbAnchor = null;
    this.stamina = this.tun.climb.stamina_max_s;
  }

  // ---- climbing: hand anchors to the mountain; body moves opposite the hand.
  // Grabbable = terrain surface within grab_radius of the hand (the 0.5 m DTM
  // gives real ledges/cliffs — this is where the accuracy work pays off).
  canGrab(handWorld) {
    const c = this.tun.climb;
    if (!c.enabled || this.stamina <= 0.5 || this.dizzyActive) return false;
    // building facades are climbable too
    if (this.collider && this.collider.grabWall(handWorld.x, handWorld.y, handWorld.z)) {
      return true;
    }
    const h = this.hf.height(handWorld.x, handWorld.z);
    if (h === null) return false;
    // near the surface, and the surface is steep enough that walking won't do
    if (Math.abs(handWorld.y - h) > c.grab_radius) return false;
    const n = this.hf.normal(handWorld.x, handWorld.z);
    if (!n) return false;
    const slopeDeg = Math.acos(Math.min(1, n.y)) * 180 / Math.PI;
    return slopeDeg > Math.min(35, c.max_slope_walkable_deg - 5);
  }

  startClimb(hand, handWorld) {
    this.climbAnchor = { hand, pos: handWorld.clone() };
    this.state = 'climb';
    this.gliding = false;
    this.vel.set(0, 0, 0);
  }

  updateClimb(dt, handWorld) {
    // the anti-trap eject owns the position while it runs; a held grip would
    // otherwise pull the body straight back into the wedge it is leaving
    if (this._unstickT > 0 || this.dizzyActive) return;
    const c = this.tun.climb;
    // body follows the inverse of hand movement, scaled by pull_strength
    const delta = _v2.copy(this.climbAnchor.pos).sub(handWorld).multiplyScalar(c.pull_strength);
    const lerp = Math.min(1, c.body_follow_lerp * dt);
    if (this.structs) this._markMove();
    this.pos.addScaledVector(delta, lerp);
    this.climbAnchor.pos.lerp(handWorld, 1 - lerp);
    this.stamina = Math.max(0, this.stamina - dt);
    this.vel.set(0, 0, 0);
    // don't sink into the mountain
    const g = this.hf.height(this.pos.x, this.pos.z);
    if (g !== null && this.pos.y < g - 1.2) this.pos.y = g - 1.2;
    // ...and don't climb THROUGH a facade. step() early-returns for state
    // 'climb' before the building resolve, so grabbing a wall and pulling used
    // to walk the body straight into the hollow interior — simultaneously the
    // main way to get stuck and the only way back out.
    this._buildingResolve(false, true);
    if (this.stamina <= 0) this.endClimb();
  }

  endClimb() {
    this.climbAnchor = null;
    if (this.state === 'climb') this.state = 'air';
  }

  // ---- jet-suit: no deploy — Player.step ignites it automatically after a
  // beat of airtime (never on the bike, never while climbing). Thrust vector
  // chases steerDir in full 3D — the ONE flight model (flat / phone: camera
  // aim; VR: the sticks' yaw + pitch, jetvr.js — the head is free look). Level flight still
  // sinks a little so landing is just flying at the ground.
  cancelGlide() {
    this.gliding = false;
    this._airT = 0;
    if (this.state === 'glide') this.state = 'air';
  }

  // thrust: the VR launch's scale on the thrust (jetvr.js, extras.jetThrust:
  // gentle after the jump, full from ~3 s); every other caller passes none = 1
  _stepJet(dt, steerDir, flare, thrust = 1) {
    const g = this.tun.glide;
    const vmax = ((g.max_speed_kmh || 100) / 3.6) * (flare ? (1 - (g.flare_brake ?? 0.65)) : 1);
    const accel = (g.accel || 14) * thrust;
    if (steerDir.lengthSq() > 0.01) _v2.copy(steerDir).normalize();
    else _v2.set(this.glideDir.x, 0, this.glideDir.z);
    const tx = _v2.x * vmax, tz = _v2.z * vmax;
    // climb is capped, level flight sinks — a jet-suit, not a helicopter
    const ty = Math.min(_v2.y * vmax, g.climb_max ?? 9) - (g.sink_speed ?? 2.2);
    const a = accel * dt;
    this.vel.x += Math.max(-a, Math.min(a, tx - this.vel.x));
    this.vel.z += Math.max(-a, Math.min(a, tz - this.vel.z));
    const av = a * 1.4;                 // vertical thrust arrests a dive quickly
    this.vel.y += Math.max(-av, Math.min(av, ty - this.vel.y));
    if (Math.hypot(this.vel.x, this.vel.z) > 1) {
      this.glideDir.set(this.vel.x, 0, this.vel.z).normalize();
    }
    const yPre = this.pos.y;   // level rule — see step()'s integration block
    if (this.structs) this._markMove();
    this.pos.addScaledVector(this.vel, dt);
    const M = 30;
    this.pos.x = Math.min(this.hf.extentX - M, Math.max(M, this.pos.x));
    this.pos.z = Math.min(this.hf.extentZ - M, Math.max(M, this.pos.z));
    // jetting through a tunnel: land on the bore floor, not the summit above
    const ground = this._groundY(this.pos.x, Math.max(this.pos.y, yPre), this.pos.z);
    if (ground === null) {
      // terrain tile still streaming — the landing clamp below cannot fire, so
      // arrest the descent instead of burrowing through unloaded ground
      if (this.vel.y < 0) this.vel.y = 0;
    } else if (this.pos.y <= ground + 0.05) {
      this.pos.y = ground;
      this.gliding = false;
      this._airT = 0;
      this.grounded = true;
      this.state = 'ground';
      this.vel.multiplyScalar(0.25);
    }
    this._buildingResolve(true);
    this.speed = Math.hypot(this.vel.x, this.vel.z);
  }

  // Walls solid, roofs landable — applied after every position integration.
  //
  // Relaxed to a fixpoint rather than resolved in one shot. A single push per
  // frame ping-ponged the body forever between two abutting footprints, and
  // `else if` meant a `ground` from ANY building silently discarded the `push`
  // from a DIFFERENT one — so standing on a low annex roof left you embedded in
  // the taller neighbour's shell with nothing pushing you out.
  // pushOnly: climbing resolves walls but must not snap onto roofs mid-pull.
  _buildingResolve(fromGlide, pushOnly) {
    if (!this.structs) { this._buildingResolve0(fromGlide, pushOnly); return; }
    this._structResolve(fromGlide, pushOnly);
    const bx = this.pos.x, bz = this.pos.z;
    this._buildingResolve0(fromGlide, pushOnly);
    // a building push is motion too: re-slide it so it can never shove the
    // body through a structure wall
    if (this.pos.x !== bx || this.pos.z !== bz) {
      const y = this.pos.y;
      const r = this.structs.slide(bx, bz, this.pos.x - bx, this.pos.z - bz,
        y + STEP_UP + STEP_EPS, y + this.eye * 0.92);
      this.pos.x = r.x; this.pos.z = r.z;
    }
  }

  // start of this frame's motion, for the structure sweep (_structResolve)
  _markMove() {
    this._sFrom.copy(this.pos);
    this._sFromOk = true;
  }

  // Baker-2 structures (game/structcol.js): the frame's motion from _markMove
  // to here, swept — floor/head crossings stop it, a slot lower than the body
  // is a wall, the lateral part slides by time of impact, an overlap is
  // pushed out within the frame budget. Then the ground is re-clamped at the
  // resolved column, exactly like the building resolve does after a push.
  _structResolve(fromGlide, pushOnly) {
    const MAX_TOTAL = Math.min(0.7, 50.4 * (this._dt || 1 / 72));
    const f = this._sFromOk ? this._sFrom : this.pos;
    this._sFromOk = false;
    const x1 = this.pos.x, z1 = this.pos.z;
    const r = this.structs.move(f.x, f.y, f.z, x1, this.pos.y, z1, this.eye * 0.92,
      this._gFn, MAX_TOTAL, this._sOut);
    this.pos.set(r.x, r.y, r.z);
    if (r.blocked) {
      this.vel.x = 0; this.vel.z = 0;
      if (this.vel.y > 0) this.vel.y = 0;
    }
    // a push-out onto ground over a step was refused (structcol move 5): the
    // body stays and stops rising, so the re-clamp below never lifts it more
    // than STEP_UP in one frame
    if ((r.ceil || r.pushRejected) && this.vel.y > 0) this.vel.y = 0;
    if (r.hit) this._impact(r.nx, r.nz);
    if (r.x !== x1 || r.z !== z1 || r.land) {
      const g = this._groundY(this.pos.x, this.pos.y, this.pos.z);
      if (g !== null && this.pos.y <= g) {
        this.pos.y = g;
        this.vel.y = Math.max(0, this.vel.y);
        this.grounded = true;
      }
    }
    if (r.land && !pushOnly) {
      this.vel.y = 0;
      this.grounded = true;
      if (fromGlide) {
        this.gliding = false;
        this._airT = 0;
        this.vel.multiplyScalar(0.25);
      }
      this.state = this.bike ? 'bike' : 'ground';
    }
  }

  // the building collider (BuildingCollision: shells, roofs, solids extras)
  _buildingResolve0(fromGlide, pushOnly) {
    if (!this.collider) return;
    // Per-frame displacement budget. Kept under the server's anti-teleport
    // speed cap (sprint_speed * 1.8 * dt + 0.5) so a resolve is never rejected
    // as a teleport, which used to leave the body a hitscan ghost. A RATE
    // capped per frame (0.7 m/frame at the 72 Hz baseline): the server's cap
    // is per 32 Hz REPORT, so a bare per-frame budget compounds with refresh —
    // a committed escape at 120 Hz pushed 2.6 m between two reports where
    // 72 Hz pushed 1.6. 50.4 m/s reproduces 72 Hz (and gate 60 Hz) behaviour
    // exactly; higher refresh now moves the same metres per second, not more.
    const MAX_TOTAL = Math.min(0.7, 50.4 * (this._dt || 1 / 72));
    let hitN = this._sweepIn();     // surface normal of the first contact, if any
    let moved = 0;
    let prevDir = null;
    // While an escape is committed the wall normals are exactly what we are
    // fleeing — let them go, or they fight the escape to a standstill.
    for (let it = 0; it < 3 && !this._escape; it++) {
      const res = this.collider.resolve(this.pos.x, this.pos.y, this.pos.z);
      if (!res.push) break;
      const { dx, dz, depth } = res.push;
      const len = Math.hypot(dx, dz) || 1;
      if (!hitN) hitN = { x: dx / len, z: dz / len };
      const room = MAX_TOTAL - moved;
      if (room <= 1e-4) break;
      // this iteration wants to undo the last one: a slot narrower than the
      // body. Stop before spending the budget bouncing across it — the escape
      // below needs some budget left to actually walk the body out.
      if (prevDir && (dx / len) * prevDir.x + (dz / len) * prevDir.z < -0.3) break;
      const step = Math.min(depth, room);
      this.pos.x += (dx / len) * step;
      this.pos.z += (dz / len) * step;
      moved += step;
      prevDir = { x: dx / len, z: dz / len };
      // trunks share the relaxation: resolving a wall used to shove the body
      // into a trunk (and vice versa) with no re-check afterwards
      const tn = this._trunkPush();
      if (tn && !hitN) hitN = tn;
      // Budget-clamped: the penetration is NOT fully resolved. Stop here rather
      // than re-querying — a partial step along a capped direction makes a
      // different edge nearest at a concave corner, and the direction thrashes
      // instead of converging. The next frame continues from a cleaner spot.
      if (step < depth - 1e-6) break;
    }
    // Still penetrating after relaxation: either wedged in a slot narrower than
    // the body (opposing walls cancel), or the nearest-edge normal simply does
    // not lead out — a self-intersecting footprint, which the bake's
    // buffer/simplify leaves on a few percent of rings. Either way no wall
    // normal will ever free it, so COMMIT to an escape target and walk there
    // over successive frames, ignoring wall pushes until free.
    //
    // It has to be multi-frame: one big jump would exceed the server's
    // anti-teleport cap, and since Sim only advances _lastValid on ACCEPTED
    // input, a rejected jump desyncs the body permanently.
    const fin = this.collider.resolve(this.pos.x, this.pos.y, this.pos.z);
    if (fin.push) {
      const fl = Math.hypot(fin.push.dx, fin.push.dz) || 1;
      const fx = fin.push.dx / fl, fz = fin.push.dz / fl;
      if (!hitN) hitN = { x: fx, z: fz };
      // Opposed to last frame's push means the body is being volleyed between
      // two walls. That case spends the WHOLE budget every frame and nets zero
      // movement, so budget-left alone would never arm the escape.
      const opposed = this._lastPush
        && fx * this._lastPush.x + fz * this._lastPush.z < -0.3;
      this._lastPush = { x: fx, z: fz };
      if (!this._escape && (moved < MAX_TOTAL || opposed)) {
        this._escape = this.collider.findFree(this.pos.x, this.pos.y, this.pos.z);
      }
      if (this._escape) {
        const ex = this._escape.x - this.pos.x, ez = this._escape.z - this.pos.z;
        const el = Math.hypot(ex, ez);
        if (el < 0.05) {
          this._escape = null;
        } else {
          // may be 0 when relaxation already spent the budget — the target is
          // still committed, and next frame skips relaxation entirely for it
          const s = Math.max(0, Math.min(el, MAX_TOTAL - moved));
          const sx = this.pos.x + (ex / el) * s, sz = this.pos.z + (ez / el) * s;
          // the walk has drifted onto a chord through a wall (the server
          // rejects it, BuildingCollision.crosses): re-arm from here
          if (this.collider.crosses(this.pos.x, this.pos.y, this.pos.z, sx, this.pos.y, sz)) {
            this._escape = null;
          } else {
            this.pos.x = sx;
            this.pos.z = sz;
            moved += s;
          }
        }
      } else {
        // No open space within reach — a contiguous village block. Slide ALONG
        // the slot rather than across it: a wedge admits no motion on the wall
        // normal, only on its tangent. Guarantees progress every frame.
        const l = Math.hypot(fin.push.dx, fin.push.dz) || 1;
        const px = -fin.push.dz / l, pz = fin.push.dx / l;
        // half the frame budget: 0.35 m/frame at the 72 Hz baseline, and the
        // slide speed stays refresh-invariant because MAX_TOTAL already is
        const step = Math.min(MAX_TOTAL * 0.5, MAX_TOTAL - moved);
        if (step > 1e-4) {
          const depthAt = (sx, sz) => {
            const q = this.collider.resolve(sx, this.pos.y, sz);
            return q.push ? q.push.depth : 0;
          };
          const sign = depthAt(this.pos.x + px * step, this.pos.z + pz * step)
            <= depthAt(this.pos.x - px * step, this.pos.z - pz * step) ? 1 : -1;
          // never along a tangent through a wall (BuildingCollision.crosses):
          // the shallower side, else the other, else stay
          for (const s of [sign, -sign]) {
            const sx = this.pos.x + px * step * s, sz = this.pos.z + pz * step * s;
            if (this.collider.crosses(this.pos.x, this.pos.y, this.pos.z, sx, this.pos.y, sz)) continue;
            this.pos.x = sx;
            this.pos.z = sz;
            moved += step;
            break;
          }
        }
      }
    } else {
      this._escape = null;      // free again — drop any committed escape
      this._lastPush = null;
    }
    if (moved <= 0) {
      const tn = this._trunkPush();
      if (tn && !hitN) hitN = tn;
    }
    if (hitN) this._impact(hitN.x, hitN.z);
    if (moved > 0) {
      const g = this._groundY(this.pos.x, this.pos.y, this.pos.z);
      if (g !== null && this.pos.y < g) {
        this.pos.y = g;
        this.vel.y = Math.max(0, this.vel.y);
        this.grounded = true;
      }
    }
    this._lastRes.set(this.pos.x, 0, this.pos.z);
    if (pushOnly) return;
    // Landing is evaluated at the RESOLVED position and independently of the
    // push above — the two are no longer mutually exclusive.
    const land = moved > 0
      ? this.collider.resolve(this.pos.x, this.pos.y, this.pos.z) : fin;
    if (land.ground !== null && this.pos.y <= land.ground + 0.15 && this.vel.y <= 0.01) {
      // A roof/deck can sit BELOW the hillside (uphill eave of a slope
      // building, a stand-only surface under rising ground). This is the last
      // write in step(), after the terrain clamp, so without the max()
      // standing on such a surface puts the camera inside the mountain.
      const g = this._groundY(this.pos.x, this.pos.y, this.pos.z);
      this.pos.y = g !== null ? Math.max(land.ground, g) : land.ground;
      this.vel.y = 0;
      this.grounded = true;
      if (fromGlide) this.gliding = false;
      this.state = 'ground';
      return;
    }
    // A deck is solid from BELOW as well: walk under a bridge freely, but the
    // head stops at the underside instead of popping through onto the deck.
    // NEVER by crushing: where the slot under a low deck is shorter than the
    // body, lowering the head pushed the feet through the terrain (measured
    // 35 corpus paths sunk up to 1.34 m once the traversal gate got real
    // ceilings) — a slot the body cannot fit is a WALL, so retreat instead.
    if (land.ceiling !== null && land.ceiling !== undefined) {
      const head = this.pos.y + this.eye * 0.92;
      if (head > land.ceiling) {
        const g = this._groundY(this.pos.x, this.pos.y, this.pos.z);
        const floorY = g !== null ? g : this.pos.y;
        if (land.ceiling - floorY >= this.eye * 0.92 + 0.05) {
          this.pos.y = land.ceiling - this.eye * 0.92;
          if (this.vel.y > 0) this.vel.y = 0;
        } else {
          this.pos.x = this._lastRes.x;
          this.pos.z = this._lastRes.z;
          if (this.vel.y > 0) this.vel.y = 0;
        }
      }
    }
  }

  // Swept wall test. Only the post-integration point was ever tested, so at
  // jet speed (~1.4 m/frame, 2.75 m in a terminal-velocity dive) any footprint
  // sliver thinner than one frame's travel was passed straight through.
  // Walks the frame's motion and stops the body at the first penetration.
  // Returns the contact normal when the sweep stopped the body, else null —
  // the caller turns it into the impact response (_impact).
  _sweepIn() {
    const px = this._lastRes.x, pz = this._lastRes.z;
    const dx = this.pos.x - px, dz = this.pos.z - pz;
    const dist = Math.hypot(dx, dz);
    if (dist <= 0.45 || dist > 60) return null;   // >60 m = a beam, not motion
    const n = Math.min(8, Math.ceil(dist / 0.4));
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const sx = px + dx * t, sz = pz + dz * t;
      const p = this.collider.resolve(sx, this.pos.y, sz).push;
      if (p) {
        this.pos.x = sx; this.pos.z = sz;
        const l = Math.hypot(p.dx, p.dz) || 1;
        return { x: p.dx / l, z: p.dz / l };
      }
    }
    return null;
  }

  // Off-road bike: heading-based physics. Throttle sets a target speed that
  // incline, surface and grip shape: anything from the roads layer is fast,
  // open terrain works below incline_max_deg, steeper is a wall.
  _steerBike(dt, extras) {
    const b = this.tun.bike;
    let steer = Math.max(-1, Math.min(1, extras.bikeSteer || 0));
    const throttle = Math.max(-1, Math.min(1, extras.bikeThrottle || 0));
    // follow mode: the route steers, not the stick/keys (which toggle the
    // indicators instead — main.js). No way in reach → ride straight.
    let follow = null;
    if (this.bikeFollow) {
      follow = this._followRoute(dt);
      steer = follow ? follow.steer : 0;
    }
    // low-speed steering is quicker (walking the bike), high-speed leans wide
    const agility = 1.2 - 0.7 * Math.min(1, this.bikeSpeed / 14);
    this.bikeYaw -= steer * (b.turn_rate_deg * Math.PI / 180) * agility * dt;
    const fx = -Math.sin(this.bikeYaw), fz = -Math.cos(this.bikeYaw);
    // slope ahead along the heading
    const h0 = this.hf.height(this.pos.x, this.pos.z);
    const h1 = this.hf.height(this.pos.x + fx * 3, this.pos.z + fz * 3);
    const slopeDeg = (h0 !== null && h1 !== null)
      ? Math.atan2(h1 - h0, 3) * 180 / Math.PI : 0;
    // surface-aware grip: the AI class map scales off-road speed (meadow is
    // the 0.75 baseline; scree crawls, plazas/asphalt count as road)
    const surf = this.surface && this.surface.at(this.pos.x, this.pos.z);
    // a route lock counts as road: rails have no road mask, but a followed
    // track bed rides like one
    const onRoad = !!follow || (this.roads && this.roads.at(this.pos.x, this.pos.z) > 0.3)
      || (surf && surf.bike >= 0.9);
    let vmax = onRoad ? b.max_speed_road
      : b.max_speed_offroad * (surf ? surf.bike / 0.75 : 1);
    if (slopeDeg > 0) {
      vmax = slopeDeg >= b.incline_max_deg
        ? 0.6                                       // too steep: crawl/stall
        : vmax * (1 - (slopeDeg / b.incline_max_deg) * 0.85);
    } else {
      vmax *= 1 + Math.min(b.downhill_boost, -slopeDeg / 45);
    }
    // rough ground (offroad, uneven normal) trims speed a bit more
    if (!onRoad) {
      const n = this.hf.normal(this.pos.x, this.pos.z, _v2n);
      if (n && n.y < 0.94) vmax *= 0.75 + 0.25 * ((n.y - 0.7) / 0.24);
    }
    if (follow) vmax *= follow.slow;   // corner / junction / dead-end braking
    const target = throttle > 0 ? throttle * vmax : 0;
    const rate = throttle < 0 ? b.brake : (target > this.bikeSpeed ? b.accel : b.brake * 0.5);
    this.bikeSpeed += Math.sign(target - this.bikeSpeed)
      * Math.min(Math.abs(target - this.bikeSpeed), rate * dt);
    if (throttle < 0) this.bikeSpeed = Math.max(0, this.bikeSpeed + throttle * b.brake * dt);
    this.vel.x = fx * this.bikeSpeed;
    this.vel.z = fz * this.bikeSpeed;
    this.bikeSteer = steer;
    // ramp launch memory: on the ground the velocity follows the surface, so
    // the uphill grade you just rode IS your vertical speed the instant the
    // lip drops away. Measured over the last 2.5 m behind the rear wheel.
    const hb = this.hf.height(this.pos.x - fx * 2.5, this.pos.z - fz * 2.5);
    this._rampVy = (h0 !== null && hb !== null)
      ? Math.max(0, Math.min(b.ramp_launch_max || 18, this.bikeSpeed * (h0 - hb) / 2.5))
      : 0;
  }

  // ---- bike auto-follow (follow mode) --------------------------------------
  // Lock onto the nearest rideable way (RoadGraph — roads, paths, rails and
  // their tunnel chords) and pure-pursue a lookahead point on its polyline;
  // _junction branches at crossings/forks by the turn indicator. Returns
  // {steer, slow} for _steerBike (slow multiplies vmax) or null when no way
  // is in reach — the bike then rides straight until one is, or Q goes manual.
  _followRoute(dt) {
    const g = this.roadnet && this.roadnet.graph;
    if (!g || !g.ways.length) return null;
    this._jCool = Math.max(0, this._jCool - this.bikeSpeed * dt);
    this._fGrace = Math.max(0, this._fGrace - this.bikeSpeed * dt);
    const b = this.tun.bike;
    const px = this.pos.x, py = this.pos.y, pz = this.pos.z;

    // refine the lock: nearest vertex of the current way around the last index
    if (this._fLock) {
      const pts = g.ways[this._fw].pts;
      let bi = this._fi, bd = Infinity;
      const i0 = Math.max(0, this._fi - 40), i1 = Math.min(pts.length - 1, this._fi + 40);
      for (let i = i0; i <= i1; i++) {
        const dx = pts[i][0] - px, dz = pts[i][2] - pz, d2 = dx * dx + dz * dz;
        if (d2 < bd) { bd = d2; bi = i; }
      }
      this._fi = bi;
      // drifted off the way (crash deflection, big air) — but not right after
      // a branch switch, when the new way is legitimately still ahead of us
      if ((bd > 100 && this._fGrace <= 0) || Math.abs(pts[bi][1] - py) > 7) {
        this._fLock = false;
      }
    }
    // acquire
    if (!this._fLock) {
      const hit = g.nearest(px, py, pz, b.follow_lock_m || 6, 5);
      if (!hit) return null;
      this._fw = hit.wi; this._fi = hit.pi; this._fLock = true;
      // ride the way in the direction the bike is already pointing (at a
      // way's last vertex the forward tangent is null: the backward one,
      // reversed — else a heading back along the way still rode off its end)
      const tb = g.tangent(this._fw, this._fi, -1);
      const t = g.tangent(this._fw, this._fi, 1) || (tb && { x: -tb.x, z: -tb.z });
      const fx = -Math.sin(this.bikeYaw), fz = -Math.cos(this.bikeYaw);
      this._fdir = (t && t.x * fx + t.z * fz < 0) ? -1 : 1;
      this._jCool = 4;
    }

    // junction decision in the window just ahead of the wheel
    if (this._jCool <= 0) this._junction(g);

    // pure-pursuit target: walk ahead of the lock by the speed-scaled lookahead
    const pts = g.ways[this._fw].pts;
    const look = Math.max(3.5, Math.min(14, this.bikeSpeed * 0.6));
    let i = this._fi, travelled = 0;
    while (travelled < look) {
      const ni = i + this._fdir;
      if (ni < 0 || ni >= pts.length) break;
      travelled += Math.hypot(pts[ni][0] - pts[i][0], pts[ni][2] - pts[i][2]);
      i = ni;
    }
    const tx = pts[i][0], tz = pts[i][2];
    const dx = tx - px, dz = tz - pz;
    let steer = 0, slow = 1;
    if (dx * dx + dz * dz > 0.25) {
      const err = wrapPi(Math.atan2(-dx, -dz) - this.bikeYaw);
      steer = Math.max(-1, Math.min(1, -err * (b.follow_steer_gain || 2.2)));
      slow = Math.max(0.15, 1 - Math.abs(err) * 1.1);
    }
    // the way runs out with no continuation: brake to a stop at its end
    if (travelled < look && (i + this._fdir < 0 || i + this._fdir >= pts.length)) {
      const de = Math.hypot(tx - px, tz - pz);
      slow = Math.min(slow, Math.max(0, (de - 1.5) / 10));
      // braked at the end: decide the continuation now. _jCool is METRES and
      // cannot run down at a standstill — a lock taken at an OSM split node
      // (the nearest vertex is a way's end, or a way shorter than the
      // debounce) held the bike at 0 m/s with W held, for good
      if (slow < 0.05) this._jCool = 0;
    }
    return { steer, slow };
  }

  // Branch choice at the junction ahead (≤ ~8 m of way from the lock): with
  // an indicator on, the branch on that side wins (any crossing or fork —
  // signed angle left = positive in this x-east/z-south frame); with it off
  // the bike stays on its way, and only a way END forces the straightest
  // continuation. Switches the lock in place; _jCool (metres) debounces so
  // one junction decides once.
  _junction(g) {
    const pts = g.ways[this._fw].pts;
    let i = this._fi, ahead = 0;
    for (let step = 0; step < 24; step++) {
      const atEnd = i + this._fdir < 0 || i + this._fdir >= pts.length;
      let tv = g.tangent(this._fw, i, this._fdir);
      if (!tv) tv = { x: -Math.sin(this.bikeYaw), z: -Math.cos(this.bikeYaw) };
      const v = pts[i], cands = [];
      g.near(v[0], v[1], v[2], 4.2, 4.5, (wi, pi) => {
        if (wi === this._fw && Math.abs(pi - i) < 12) return;   // own locality
        for (const d of [1, -1]) {
          const tc = g.tangent(wi, pi, d);
          if (!tc) continue;
          const ang = Math.atan2(tv.z * tc.x - tv.x * tc.z, tv.x * tc.x + tv.z * tc.z);
          if (Math.abs(ang) > 2.7) continue;                    // never a u-turn
          cands.push({ wi, pi, d, ang });
        }
      });
      let pick = null;
      if (this.bikeInd !== 0 && cands.length) {
        // indicated side only, ≥ ~17° off-axis (a parallel way is not a turn)
        let best = Infinity;
        for (const c of cands) {
          if (this.bikeInd === -1 ? c.ang < 0.3 : c.ang > -0.3) continue;
          const score = Math.abs(Math.abs(c.ang) - 1.1);        // prefer a clean turn
          if (score < best) { best = score; pick = c; }
        }
      }
      if (!pick && atEnd && cands.length) {
        let best = Infinity;
        for (const c of cands) {
          if (Math.abs(c.ang) > 1.9) continue;
          if (Math.abs(c.ang) < best) { best = Math.abs(c.ang); pick = c; }
        }
      }
      if (pick) {
        this._fw = pick.wi; this._fi = pick.pi; this._fdir = pick.d;
        this._jCool = 15;
        this._fGrace = 14;
        return;
      }
      if (atEnd) return;
      const ni = i + this._fdir;
      ahead += Math.hypot(pts[ni][0] - pts[i][0], pts[ni][2] - pts[i][2]);
      if (ahead > 8) return;
      i = ni;
    }
  }

  // moveDir: desired horizontal move in WORLD space (unit-ish), sprint bool
  // THE TIME STEP (vr-judder, 2026-11-01): a frame consumes its WHOLE real
  // interval. The integrator's step is bounded (STEP_MAX_S: the collision
  // push bound is a speed, the landing clamp one skin per step), so a frame
  // longer than the bound runs several equal sub-steps instead of losing the
  // rest — the old `dt = min(dt, 0.05)` dropped 0–25 % of every 50–66 ms
  // frame (a Quest at 15–24 fps: half its frames) and 40–65 % of a 90–140 ms
  // one, so the body's speed wobbled with the frame time: the irregular
  // "one frame is not right" judder in the headset (session 870). The jump
  // edge fires in the first sub-step only; every timer / ease takes the
  // sub-step's dt. The tick's own cap is the hitch (main.js DT_MAX_S).
  // extras.headMove {x, z} (world m; VR, main.js headFold): the physical
  // head's displacement since the last tick — a BODY move, resolved by the
  // same rules as a stick move (the wall push, the span containment, the
  // ceiling cap, the landing clamp) in the first sub-step; see _headMove.
  step(dt, moveDir, sprint, jump, extras = {}) {
    const n = dt > STEP_MAX_S ? Math.ceil(dt / STEP_MAX_S) : 1;
    const h = dt / n;
    for (let i = 0; i < n; i++) this._stepOnce(h, moveDir, sprint, i === 0 && jump, extras, i === 0 ? extras.headMove : null);
  }

  // THE BODY STANDS UNDER THE HEAD (vr-sink, 2026-11-01): in VR the rig
  // origin is the body and the head sits wherever the headset says, so a
  // physical step or lean left the body (and its standing point) behind: on
  // a 30-42 deg bank the ground under the EYE was up to 1.25 m over the
  // ground under the FEET within 1.5 m (session 872's walk), and the owner
  // stood knee- to waist-deep in the drawn slope ("sinks into the
  // territory"; never on desktop, whose eye is over the feet). So the head's
  // displacement moves the body, under the walk's own limits: a rise steeper
  // than slope_walk_max_deg (over STEP_UP) or a ground not resident refuses
  // the move — the body stays, and main.js re-bases the rig to it, so the
  // view holds at the body (a wall, a cliff: the world does not follow the
  // head through it). Returns whether the move was taken.
  _headMove(hm) {
    if (!hm) return false;
    const run = Math.hypot(hm.x, hm.z);
    if (!(run > 1e-6)) return false;
    const x1 = this.pos.x + hm.x, z1 = this.pos.z + hm.z;
    const g1 = this._groundY(x1, this.pos.y, z1);
    if (g1 === null) return false;
    const lim = this.tun.locomotion.slope_walk_max_deg ?? 42;
    const band = Math.max(STEP_UP, run * Math.tan(lim * Math.PI / 180));
    if (g1 - this.pos.y > band) return false;
    this.pos.x = x1;
    this.pos.z = z1;
    // a grounded body steps DOWN onto the ground within the same band (the
    // clamp below only lifts: it would hover, then fall); past it (an edge)
    // it is in the air, as a walk off the edge is
    if (this.grounded && this.pos.y > g1 && this.pos.y - g1 <= band) this.pos.y = g1;
    return true;
  }

  _stepOnce(dt, moveDir, sprint, jump, extras, headMove = null) {
    const t = this.tun.locomotion;
    this._dt = dt;                 // the collision push bound is a speed, not a step
    this.hf.ensureAround(this.pos.x, this.pos.z);

    // dizzy death mode: frozen = fully locked in place (before _antiStuck —
    // nothing may move the body); then the dizzy decay allows walking only
    if (this.dizzyFreeze > 0) {
      this.dizzyFreeze = Math.max(0, this.dizzyFreeze - dt);
      this.vel.set(0, 0, 0);
      this.speed = 0;
      this.state = 'ground';
      return;
    }
    if (this.dizzyT > 0) {
      this.dizzyT = Math.max(0, this.dizzyT - dt);
      sprint = false;
      jump = false;
    }

    // anti-trap watchdog FIRST: while it is ejecting the body nothing else may
    // touch the position, or the very constraints that trapped it re-trap it
    // on the way out (climb/glide return early below, so it cannot live at the
    // end of the function)
    this._antiStuck(dt, moveDir.lengthSq() > 0.01 || !!jump || this.gliding
      || (this.bike && Math.abs(extras.bikeThrottle || 0) > 0.05));
    // Two separate things can make a body refuse to walk, and from inside the
    // headset they feel identical ("forward does nothing, then after a few
    // jumps it frees up"): the anti-stuck ejection above, which suspends ALL
    // movement for up to 4 s, and the slope brake further down, which damps
    // horizontal velocity to 15 % while you lean into a face steeper than
    // slope_walk_max_deg. brakedT/_unstickT are surfaced on the HUD so the two
    // can be told apart on sight instead of guessed at.
    this.brakedT = Math.max(0, (this.brakedT || 0) - dt);
    this._stepCrouch(dt);
    if (this._unstickT > 0) { this.speed = 0; return; }

    // stamina regen whenever not climbing
    if (this.state !== 'climb') {
      this.stamina = Math.min(this.tun.climb.stamina_max_s,
        this.stamina + this.tun.climb.stamina_regen_per_s * dt);
    }
    if (this.state === 'climb') { this.speed = 0; return; }  // driven by updateClimb
    if (this.gliding) { this._stepJet(dt, extras.steerDir || moveDir, !!extras.flare, extras.jetThrust ?? 1); return; }

    // jet-suit auto-ignition: a beat of airtime past the jump apex, never on
    // the bike (bike jumps stay ballistic), never while stunned by a crash
    // (_impact) — there is no deploy button. NEVER under a roof: a jump inside
    // a bore reaches the ignition airtime, and an ignited jet carries the body
    // forward out of the footprint and lands it on the mountain — measured as
    // 51 of the 66 jump-through-roof escapes. The roof is the spans column's
    // ceiling OR a structure ceiling within ROOF_JET_M over the
    // feet: an open-sky span column under a lining (the tube's own crown is
    // its roof, the column carries none) let the jet ignite inside rail
    // sidings and carry a still body out of the tube end onto the mountain
    // (S6.b fix round, sid window: 2 of 82 roofed stations).
    if (this._crashT > 0) this._crashT = Math.max(0, this._crashT - dt);
    const gj = this.tun.glide;
    const roofed = (() => {
      const r = this._spanAt(this.pos.x, this.pos.y, this.pos.z);
      if (r !== null && r.ceiling !== null) return true;
      if (!this.structs) return false;
      const c = this.structs.ceilingAt(this.pos.x, this.pos.z, this.pos.y);
      return c !== null && c - this.pos.y <= ROOF_JET_M;
    })();
    if (gj && gj.enabled && !this.grounded && !this.bike && this._crashT <= 0
        && !roofed && this.dizzyT <= 0) {
      this._airT += dt;
      if (this._airT >= (gj.activate_delay_s ?? 0.4) && this.vel.y < 1) {
        this.gliding = true;
        this.state = 'glide';
        this.glideDir.set(this.vel.x, 0, this.vel.z);
        if (this.glideDir.lengthSq() < 0.01) {
          this.glideDir.copy(extras.steerDir || moveDir).setY(0);
        }
        if (this.glideDir.lengthSq() < 0.01) this.glideDir.set(0, 0, -1);
        this.glideDir.normalize();
        this._stepJet(dt, extras.steerDir || moveDir, !!extras.flare, extras.jetThrust ?? 1);
        return;
      }
    } else {
      this._airT = 0;
    }

    const surf = this._skinY(this.pos.x, this.pos.z);
    if (surf === null) return; // collision tile not resident yet — hold position
    // inside a rail tunnel bore the walkable floor is the chord bed, not the
    // mountain surface above (_groundY applies that only while genuinely below
    // the surface — over a shallow portal roof it must never fire, or walking
    // across the portal sucks the player down through the terrain)
    const ground = this._groundY(this.pos.x, this.pos.y, this.pos.z);
    const inBore = ground < surf - 0.5;   // a span hole's bed won the clamp

    // swimming: body in a lake, floating at the surface (see _groundY)
    const wsurf = this.water ? this.water.surfaceAt(this.pos.x, this.pos.z) : null;
    const wcfg = this.tun.water || {};
    const wasSwim = this.swimming;
    this.swimming = wcfg.enabled !== false && wsurf !== null
      && this.pos.y < wsurf - 0.2;
    // Water entry: a splash impulse keyed to how hard the body hit the sheet
    // (consumed by main -> Sfx.splash); wading out gets a small one. The bike
    // dismounts — a floating motocross bike breaks the fiction — while the
    // jet-suit stays armed: igniting it is how you LEAVE the lake.
    if (this.swimming !== wasSwim) {
      this.splash = this.swimming
        ? Math.min(1, (Math.abs(this.vel.y) * 0.8 + this.speed * 0.25) / 8)
        : 0.18;
      if (this.swimming && this.bike) this.bike = false;
    }
    // Swim presentation blend + head target (see the eye getter / the VR rig
    // nudge in main.js): eased so the camera never snaps at the shoreline.
    this._swimK = this.swimming
      ? Math.min(1, this._swimK + dt * 3) : Math.max(0, this._swimK - dt * 3);
    if (this.swimming) {
      this._swimSurf = wsurf;
      this._swimEyeRel = Math.min(2.5,
        Math.max(0.25, wsurf + (wcfg.eye_over_m ?? 0.35) - this.pos.y));
    } else if (this._swimK === 0) {
      this._swimSurf = null; this._swimEyeRel = null;
    }

    if (this.bike && this.grounded) {
      this._steerBike(dt, extras);
    } else if (!this.bike) {
      const target = (sprint ? t.sprint_speed : t.walk_speed)
        * (this.swimming ? (wcfg.swim_speed_mult || 0.32) : 1)
        * (this.dizzyT > 0 ? this._dizzyMult() : 1);
      const accel = this.grounded ? 30 : 30 * t.air_control;
      this.vel.x += (moveDir.x * target - this.vel.x) * Math.min(1, accel * dt / target);
      this.vel.z += (moveDir.z * target - this.vel.z) * Math.min(1, accel * dt / target);

      // slope wall: refuse to walk up faces steeper than max.
      // The probe distance is FIXED, along the INPUT direction — it used to be
      // vel*dt*4, which shrinks with speed: once anything slowed the body, a
      // 0.22 m terrain lip at a 6 mm lookahead read as an 88° wall, the brake
      // re-fired every frame and the crawl never recovered (measured at the
      // Inn bridge landings — the same lip is 32° at 3 m/s and flat at sprint).
      // A fixed probe makes the same face read the same angle at every speed.
      const PROBE = 0.6;
      const aheadX = this.pos.x + moveDir.x * PROBE;
      const aheadZ = this.pos.z + moveDir.z * PROBE;
      const hAhead = this.hf.height(aheadX, aheadZ);
      // The ground ahead is span-aware: a bore mouth shows its bed (no fake
      // rise, no brake) while the cliff beside it shows the mountain (full
      // brake — unbraked, a sprint up such a face lifts the body past Sim's
      // capUp and the authoritative correction snaps the player back).
      let aheadG = hAhead;
      if (this.spans && hAhead !== null) {
        const ra = this.spans.resolve(aheadX, aheadZ, this.pos.y, hAhead);
        if (ra !== null) aheadG = ra.ground;
      }
      if (hAhead !== null && this.grounded) {
        // rise is measured from the FEET, not the terrain sample: standing on
        // a structure the terrain runs below the soles, and a terrain-based
        // rise reads the deck lip as a wall while boarding it. Anything the
        // step-up allowance absorbs (< 0.35 m) is a curb, never a wall —
        // without the floor, bilinear noise brakes a stationary body.
        const base = Math.max(ground, this.pos.y);
        const rise = aheadG - base;
        // on a ground flight (here or the probe point) the stair limit
        const fl = this.roadnet && this.roadnet.flightAt;
        const lim = rise > 0.35 && fl && (this.roadnet.flightAt(this.pos.x, this.pos.z) || this.roadnet.flightAt(aheadX, aheadZ))
          ? Math.max(t.slope_walk_max_deg, GROUND_FLIGHT_WALK_DEG) : t.slope_walk_max_deg;
        if (rise > 0.35 && Math.atan2(rise, PROBE) > lim * Math.PI / 180) {
          this.vel.x *= 0.15; this.vel.z *= 0.15;
          this.brakedT = 0.25;   // HUD readout — see below
        }
      }
    }

    this.vel.y -= t.gravity * dt;
    // free-fall terminal velocity (comfort + keeps the server caps sane)
    const term = (this.tun.glide && this.tun.glide.terminal_fall_speed) || 55;
    if (this.vel.y < -term) this.vel.y = -term;
    if (this.grounded && jump && !this.swimming) {
      // bike hop stacks on the ramp-follow speed — jumping AT the lip is the
      // big-air move (physically additive, capped by ramp_launch_max)
      this.vel.y = this.bike
        ? Math.min((this.tun.bike.ramp_launch_max || 18) + 4,
          (this.tun.bike.hop_speed || 9) + this._rampVy)
        : t.jump_speed;
    }

    // The level the body legitimately occupied BEFORE this frame's motion.
    // The landing clamp below resolves the ground at max(yPre, pos.y): a fall
    // can then never skip through a hole's crown in one integration step —
    // starting above the crown, the resolver's slab rule answers the slab TOP
    // and the body lands on the drawn skin instead of dropping through it
    // into the hole (the knife-edge trap, APP_BAKER §6 SOLID IS SOLID). Entering a
    // hole stays possible exactly where it should be: through a portal
    // (open-sky columns are level-independent) or laterally at bed level.
    const yPre = this.pos.y;
    if (this.structs) this._markMove();
    this._headMove(headMove);      // the head's step first: the same resolvers below
    this.pos.x += this.vel.x * dt;
    this.pos.z += this.vel.z * dt;
    this.pos.y += this.vel.y * dt;

    // playable bounds: soft-wall 30 m inside the data edge
    const M = 30;
    this.pos.x = Math.min(this.hf.extentX - M, Math.max(M, this.pos.x));
    this.pos.z = Math.min(this.hf.extentZ - M, Math.max(M, this.pos.z));

    // Span containment is column membership: one column outside a hole's
    // footprint has ground = mountain, and the ordinary step-up/brake rejects
    // it. A step out of a hole into a no-verdict column is an exit only when
    // you could WALK there: a destination far BELOW the bed is the wall, one
    // far ABOVE the feet is the ROOF (with no hole there, the terrain clamp
    // would lift the body straight up through the drawn lining onto the
    // surface — measured at the station subway's stair-foot elbow, 1728 ->
    // 1730 onto the plaza). The test is the EFFECTIVE ground (_groundY).
    if (inBore && this.spans
      && this._spanAt(this.pos.x, this.pos.y, this.pos.z) === null) {
      const gOut = this._groundY(this.pos.x, this.pos.y, this.pos.z);
      if (gOut !== null && (gOut < ground - 0.75 || gOut > this.pos.y + 0.75)) {
        this.pos.x = this._lastRes.x;
        this.pos.z = this._lastRes.z;
      }
    }
    // the crown is solid: cap the head inside the bore. The ceiling comes
    // from the SAME resolve as the ground, gate-free: above the roof it is
    // null by construction, so nothing can shove a surface walker, and the
    // emitter's crawl coverage makes the span answer complete — a no-verdict
    // column has no ceiling because it has no hole.
    const rCeil = this._spanAt(this.pos.x, this.pos.y, this.pos.z);
    const bceil = rCeil ? rCeil.ceiling : null;
    if (bceil !== null) {
      const head = this.pos.y + this.eye * 0.92;
      if (head > bceil) {
        this.pos.y = bceil - this.eye * 0.92;
        if (this.vel.y > 0) this.vel.y = 0;
      }
    }
    const g2 = this._groundY(this.pos.x, Math.max(this.pos.y, yPre), this.pos.z);
    if (g2 !== null) {
      if (this.pos.y <= g2) {
        this.pos.y = g2;
        this.vel.y = Math.max(0, this.vel.y);
        this.grounded = true;
        this.state = this.swimming ? 'swim' : (this.bike ? 'bike' : 'ground');
      } else {
        const wasGrounded = this.grounded;
        this.grounded = this.pos.y - g2 < 0.08;
        if (!this.grounded) {
          if (this.bike && wasGrounded && this._rampVy > 0.5) {
            // the lip just dropped away under the bike: carry the surface-follow
            // vertical speed ballistically, with a gamified boost on top
            const b = this.tun.bike;
            this.vel.y = Math.max(this.vel.y, Math.min(b.ramp_launch_max || 18,
              this._rampVy * (b.ramp_boost || 1.35)));
          }
          if (this.state === 'ground') this.state = 'air';
        }
      }
    }
    this._buildingResolve(false);
    this.speed = Math.hypot(this.vel.x, this.vel.z);
  }
}

// ---------------------------------------------------------------- flat input
export class FlatControls {
  constructor(dom) {
    this.keys = new Set();
    this.lookYaw = 0;
    this.lookPitch = 0;
    this.fireHeld = false;
    this.locked = false;
    this.active = false;       // flat mode is up (main.js): the right button is ours
    // look sensitivity × this: the view's fov / fov0 (main.js, viewzoom.js) —
    // a zoomed view turns as many PIXELS per mouse count as at 1×
    this.lookScale = 1;
    // right button: an EDGE (no hold state). onRmb runs synchronously inside
    // the mousedown (main.js toggles World ⇄ Map there — re-acquiring pointer
    // lock needs the gesture); rmbPressed latches it for a polling reader.
    this.rmbPressed = false;
    this.onRmb = null;
    dom.addEventListener('click', () => {
      if (!this.locked) dom.requestPointerLock();
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === dom;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      const k = 0.0022 * this.lookScale;
      this.lookYaw -= e.movementX * k;
      this.lookPitch = Math.max(-1.5, Math.min(1.5, this.lookPitch - e.movementY * k));
    });
    document.addEventListener('keydown', (e) => this.keys.add(e.code));
    document.addEventListener('keyup', (e) => this.keys.delete(e.code));
    document.addEventListener('mousedown', (e) => {
      if (this.locked && e.button === 0) this.fireHeld = true;
      if (e.button === 2 && this.active) {
        this.rmbPressed = true;
        if (this.onRmb) this.onRmb(e);
      }
    });
    document.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.fireHeld = false;
    });
    document.addEventListener('contextmenu', (e) => { if (this.locked || this.active) e.preventDefault(); });
  }

  // the right-button edge since the last call
  consumeRmb() {
    if (!this.rmbPressed) return false;
    this.rmbPressed = false;
    return true;
  }

  moveDir(out) {
    let f = 0, s = 0;
    if (this.keys.has('KeyW')) f += 1;
    if (this.keys.has('KeyS')) f -= 1;
    if (this.keys.has('KeyD')) s += 1;
    if (this.keys.has('KeyA')) s -= 1;
    const sin = Math.sin(this.lookYaw), cos = Math.cos(this.lookYaw);
    out.set(-sin * f + cos * s, 0, -cos * f - sin * s);
    if (out.lengthSq() > 1) out.normalize();
    return out;
  }

  get sprint() { return this.keys.has('ShiftLeft'); }
  get jump() { return this.keys.has('Space'); }
}

// ---------------------------------------------------------------- VR input
export class VRControls {
  constructor(renderer, player) {
    this.renderer = renderer;
    this.player = player;
    this.snapArmed = true;     // the snap turn re-arms when the stick comes back
    this.session = null;
    this.lastButtons = [{}, {}];
    this.lastMenu = false;
    // the snap flick's thresholds (tunables.locomotion snap_turn_arm / _rearm)
    this.snapArm = 0.7;
    this.snapRearm = 0.3;
    this._rAxis = 0;           // the right stick's locked axis: 0 none, 1 X (turn), 2 Y (zoom)
    this._rClick = false;      // the right stick click latch: set on the press, cleared back in the dead zone
    // ONE output object, refilled every poll (no per-frame allocation)
    this._out = { sprint: false, jump: false, snap: 0, turnX: 0, zoomY: 0, menu: false, mapToggle: false,
      faceA: false, faceB: false, faceX: false, faceY: false, fire: [], grips: [], axL: { x: 0, y: 0 },
      ctrl: 0, wake: false };
    // the stick gate (vrstick.js, w57): an untouched stick reads 0, its rest offset learned
    this._gate = { left: new StickGate(), right: new StickGate() };
    this._wake = false;        // the sources changed: the next poll seeds the press state (sourcesChanged)
    const pool = () => [0, 1, 2, 3].map((index) => ({ hand: '', pressed: false, touched: false, index }));
    this._fire = pool();       // ≤ 4 sources (two controllers + two tracked hands)
    this._grip = pool();
  }

  // the press edge of gp.buttons[i] since the last poll, per hand slot + key
  _edge(gp, i, slot, key) {
    const down = !!(gp.buttons[i] && gp.buttons[i].pressed);
    const was = this.lastButtons[slot][key];
    this.lastButtons[slot][key] = down;
    return down && !was && !this._wake;
  }

  // the session's 'inputsourceschange' (main.js): a controller woke or slept
  // (Quest: hand tracking takes over while they rest, vr-redon). poll reads
  // session.inputSources fresh every frame — no source or gamepad is ever
  // kept — so the only state to renew is the press edges: the next poll
  // seeds them from the buttons as they are, and the press that woke a
  // controller does nothing else (out.wake: vrui.js seeds its triggers too).
  sourcesChanged() { this._wake = true; this._gate.left.reset(); this._gate.right.reset(); }

  // returns { sprint, jump, snap, turnX, zoomY, menu, mapToggle, faceA, faceB,
  // faceX, faceY, fire:[{hand,pressed,touched,index}], grips, axL } — the
  // owner's map (2026-11-01, APP_PLAYER "VR controls"): LEFT stick = WASD
  // (main.js moves along the gaze heading; the jet-suit: jetvr.js, Y pitch + X steer;
  // full deflection = sprint), LEFT stick click = the Map panel, LEFT
  // trigger = the Map panel / RIGHT trigger = the data at the point (vrui.js,
  // both click a panel), RIGHT stick X = the comfort turn (turnX raw for
  // smooth, snap = ±1 per flick past snapArm, re-armed below snapRearm), RIGHT
  // stick Y = the view zoom (zoomY), RIGHT stick click = jump. The right stick
  // locks to its dominant axis until it comes back to centre, so a zoom push
  // never turns and a turn never zooms; the stick CLICK latches both off
  // until the stick is centred again (_rClick). Face buttons are edges (main.js: A
  // the ☰ menu (owner 2026-11-03), B back / close · station 2nd row, X bike,
  // Y mic · bike riding method). Triggers + grips are level (vrui.js owns the
  // triggers). menu = the press edge of any button past the xr-standard six
  // (Quest keeps ☰ to itself; some PCVR runtimes expose it) — A is the menu
  // everywhere. Both sticks pass the STICK GATE first (vrstick.js, w57): an
  // untouched stick reads 0 (the owner's right stick rests at x ≈ 0.96).
  poll(dt) {
    const session = this.renderer.xr.getSession();
    const out = this._out;
    out.sprint = false; out.jump = false; out.snap = 0; out.turnX = 0; out.zoomY = 0; out.menu = false;
    out.mapToggle = false; out.faceA = false; out.faceB = false; out.faceX = false; out.faceY = false;
    out.fire.length = 0; out.grips.length = 0; out.axL.x = 0; out.axL.y = 0;
    out.ctrl = 0; out.wake = this._wake;
    if (!session) { this._wake = false; return out; }
    let si = 0, menu = false;
    for (const src of session.inputSources) {
      const gp = src.gamepad;
      if (!gp) continue;
      if (!src.hand) out.ctrl++;               // a controller (a tracked hand is never one)
      const rawX = gp.axes[2] !== undefined ? gp.axes[2] : gp.axes[0] || 0;
      const rawY = gp.axes[3] !== undefined ? gp.axes[3] : gp.axes[1] || 0;
      const gate = this._gate[src.handedness === 'left' ? 'left' : 'right'];
      const gs = (src.handedness === 'left' || src.handedness === 'right')
        ? gate.update(rawX, rawY, gp.buttons[3], dt) : null;
      const axX = gs ? gs.x : rawX, axY = gs ? gs.y : rawY;
      if (src.handedness === 'left') {
        out.sprint = Math.hypot(axX, axY) > 0.92;
        out.axL.x = axX; out.axL.y = axY;      // raw: walk / bike / jet-suit (main.js)
        if (this._edge(gp, 3, 0, 'stick')) out.mapToggle = true;
        if (this._edge(gp, 4, 0, 'x')) out.faceX = true;
        if (this._edge(gp, 5, 0, 'y')) out.faceY = true;
      } else if (src.handedness === 'right') {
        // axis lock: the first axis past 0.25 owns the stick until it is
        // back under 0.15 (the dead zone) on both axes
        const ax = Math.abs(axX), ay = Math.abs(axY);
        // the stick CLICK (jump) tilts the stick: from the press until the
        // stick is back in the dead zone it neither turns nor zooms (the
        // press used to raise the head zoom window — vr-nomap)
        const rDown = !!(gp.buttons[3] && gp.buttons[3].pressed);
        if (rDown) this._rClick = true;
        else if (this._rClick && ax < 0.15 && ay < 0.15) this._rClick = false;
        if (this._rClick || (ax < 0.15 && ay < 0.15)) this._rAxis = 0;
        else if (this._rAxis === 0 && Math.max(ax, ay) > 0.25) this._rAxis = ax >= ay ? 1 : 2;
        const tx = this._rAxis === 1 ? axX : 0;
        out.turnX = tx;
        out.zoomY = this._rAxis === 2 ? axY : 0;
        if (this.snapArmed && Math.abs(tx) > this.snapArm) {
          out.snap = Math.sign(tx);
          this.snapArmed = false;
        } else if (!this.snapArmed && ax < this.snapRearm) this.snapArmed = true;
        if (this._edge(gp, 3, 1, 'stick')) out.jump = true;
        if (this._edge(gp, 4, 1, 'a')) out.faceA = true;
        if (this._edge(gp, 5, 1, 'b')) out.faceB = true;
      }
      for (let b = 7; b < gp.buttons.length; b++) if (gp.buttons[b] && gp.buttons[b].pressed) menu = true;
      const tb = gp.buttons[0];
      const grip = gp.buttons[1] && gp.buttons[1].pressed;
      if (si < 4) {
        const f = this._fire[si], g = this._grip[si];
        // touched: the finger rests on the trigger (Quest's capacitive
        // sensor; a runtime without one reports it with the press)
        f.hand = src.handedness; f.pressed = !!(tb && tb.pressed); f.touched = !!(tb && (tb.touched || tb.pressed));
        f.index = si;
        g.hand = src.handedness; g.pressed = !!grip; g.index = si;
        out.fire.push(f); out.grips.push(g);
      }
      si++;
    }
    if (menu && !this.lastMenu && !this._wake) out.menu = true;
    this.lastMenu = menu;
    this._wake = false;
    return out;
  }
}

// comfort vignette: camera-attached ring whose opacity follows speed
export class Vignette {
  constructor(camera) {
    const geo = new THREE.RingGeometry(0.42, 1.6, 32);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x000000, transparent: true, opacity: 0, side: THREE.DoubleSide,
      depthTest: false, depthWrite: false, fog: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.z = -0.6;
    this.mesh.renderOrder = 999;
    this.mesh.visible = false;
    camera.add(this.mesh);
    this.level = 0;
  }

  update(dt, speed, state, tun) {
    const c = tun.comfort;
    if (!c.vignette_enabled) { this.mesh.visible = false; return; }
    let target = 0;
    if (speed > c.vignette_speed_min) {
      target = Math.min(1, (speed - c.vignette_speed_min)
        / Math.max(0.5, c.vignette_speed_max - c.vignette_speed_min));
    }
    if (state === 'glide') target = Math.min(1, target + c.glide_vignette_boost);
    target *= c.vignette_strength;
    this.level += (target - this.level) * Math.min(1, dt * 8);
    this.mesh.material.opacity = this.level;
    this.mesh.visible = this.level > 0.02;
  }
}

function wrapPi(a) {
  a = a % (2 * Math.PI);
  if (a > Math.PI) a -= 2 * Math.PI;
  if (a < -Math.PI) a += 2 * Math.PI;
  return a;
}

const _v2 = new THREE.Vector3();
const _v2n = new THREE.Vector3();
